import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Edit2, Trash2, X } from 'lucide-react';
import { API_BASE } from '../../lib/api';

const API = API_BASE;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });

const CATS = ['digital_marketing', 'social_media', 'branding', 'project_management', 'content_creation', 'event_production'];
const EMPTY = {
  client: '',
  title_es: '',
  title_en: '',
  description_es: '',
  description_en: '',
  category: 'digital_marketing',
  thumbnail: '',
  results_es: '',
  results_en: '',
  external_links: [],
  published: true,
};

const PLATFORM_FIELDS = [
  { key: 'iheart', label: 'iHeart' },
  { key: 'spotify', label: 'Spotify' },
  { key: 'apple-podcasts', label: 'Apple Podcasts' },
  { key: 'youtube', label: 'YouTube' },
  { key: 'amazon-music', label: 'Amazon Music' },
];

const linksToForm = (links = []) => {
  const mapped = Object.fromEntries(PLATFORM_FIELDS.map(({ key }) => [key, '']));
  links.forEach((link) => {
    if (link?.href && mapped[link.label?.toLowerCase?.().replace(/\s+/g, '-')] !== undefined) {
      mapped[link.label.toLowerCase().replace(/\s+/g, '-')] = link.href;
    }
  });
  return mapped;
};

const formToLinks = (platforms) =>
  PLATFORM_FIELDS
    .map(({ key, label }) => ({ label, href: (platforms[key] || '').trim() }))
    .filter((link) => link.href);

const AdminCases = () => {
  const [cases, setCases] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [platforms, setPlatforms] = useState(() => linksToForm());
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const fetchCases = () => {
    axios.get(`${API}/admin/cases`, getAuth())
      .then(res => setCases(res.data))
      .catch(e => { if (e.response?.status === 401) navigate('/admin'); });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchCases(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setPlatforms(linksToForm());
    setShowForm(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      client: c.client,
      title_es: c.title_es,
      title_en: c.title_en,
      description_es: c.description_es,
      description_en: c.description_en,
      category: c.category,
      thumbnail: c.thumbnail || '',
      results_es: c.results_es || '',
      results_en: c.results_en || '',
      external_links: c.external_links || [],
      published: c.published,
    });
    setPlatforms(linksToForm(c.external_links));
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, external_links: formToLinks(platforms) };
    try {
      if (editing) { await axios.put(`${API}/admin/cases/${editing.id}`, payload, getAuth()); }
      else { await axios.post(`${API}/admin/cases`, payload, getAuth()); }
      fetchCases();
      setShowForm(false);
    } catch { alert('Error al guardar'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este caso?')) return;
    await axios.delete(`${API}/admin/cases/${id}`, getAuth());
    fetchCases();
  };

  const F = (key, label, required = false, type = 'input', rows = 3) => (
    <div>
      <label className="block text-white/50 text-xs mb-1.5 font-source-sans uppercase tracking-wide">{label}{required ? ' *' : ''}</label>
      {type === 'input'
        ? <input required={required} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} className="w-full bg-[#0F1C2E] border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-[#7D468C]" />
        : <textarea rows={rows} required={required} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} className="w-full bg-[#0F1C2E] border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-[#7D468C] resize-none" />}
    </div>
  );

  return (
    <div className="p-8" data-testid="admin-cases-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-poppins font-bold text-2xl text-white">Casos de Estudio</h1>
          <p className="text-white/40 text-sm font-source-sans mt-1">{cases.length} proyectos</p>
        </div>
        <button onClick={openCreate} data-testid="create-case-btn" className="flex items-center gap-2 bg-[#4EBFBF] text-white px-5 py-2.5 font-poppins font-semibold text-sm hover:bg-[#76DDDD] transition-colors">
          <Plus size={16} /> Nuevo Caso
        </button>
      </div>

      <div className="bg-[#1A2639] border border-white/8 overflow-hidden">
        {cases.length === 0 ? (
          <div className="text-center py-16 text-white/30 font-source-sans">No hay casos aún.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-white/8">
              <tr>
                <th className="text-left px-6 py-3 text-white/40 font-poppins font-semibold text-xs uppercase tracking-wide">Cliente / Título</th>
                <th className="text-left px-4 py-3 text-white/40 font-poppins font-semibold text-xs uppercase tracking-wide hidden md:table-cell">Categoría</th>
                <th className="text-left px-4 py-3 text-white/40 font-poppins font-semibold text-xs uppercase tracking-wide hidden lg:table-cell">Estado</th>
                <th className="text-right px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {cases.map(c => (
                <tr key={c.id} data-testid={`case-row-${c.id}`} className="border-b border-white/5 hover:bg-white/3 transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-poppins font-medium text-white">{c.client}</div>
                    <div className="text-white/35 text-xs font-source-sans mt-0.5 line-clamp-1">{c.title_es}</div>
                  </td>
                  <td className="px-4 py-4 text-white/50 font-source-sans hidden md:table-cell text-xs">{c.category}</td>
                  <td className="px-4 py-4 hidden lg:table-cell">
                    <span className={`text-xs font-poppins font-semibold px-2 py-0.5 ${c.published ? 'bg-green-500/15 text-green-400' : 'bg-white/8 text-white/40'}`}>
                      {c.published ? 'Publicado' : 'Oculto'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openEdit(c)} className="p-1.5 text-white/40 hover:text-[#4EBFBF] transition-colors"><Edit2 size={14} /></button>
                      <button onClick={() => handleDelete(c.id)} className="p-1.5 text-white/40 hover:text-red-400 transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="case-form-modal">
          <div className="bg-[#1A2639] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-white/8">
              <h2 className="font-poppins font-bold text-white">{editing ? 'Editar Caso' : 'Nuevo Caso'}</h2>
              <button onClick={() => setShowForm(false)} className="text-white/40 hover:text-white"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {F('client', 'Cliente', true)}
                <div>
                  <label className="block text-white/50 text-xs mb-1.5 font-source-sans uppercase tracking-wide">Categoría</label>
                  <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full bg-[#0F1C2E] border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-[#7D468C]">
                    {CATS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {F('title_es', 'Título ES', true)}
                {F('title_en', 'Título EN', true)}
              </div>
              {F('thumbnail', 'URL Thumbnail')}
              <div className="grid grid-cols-2 gap-4">
                {F('description_es', 'Descripción ES', true, 'textarea')}
                {F('description_en', 'Descripción EN', true, 'textarea')}
              </div>
              <div className="grid grid-cols-2 gap-4">
                {F('results_es', 'Resultados ES', false, 'textarea', 2)}
                {F('results_en', 'Resultados EN', false, 'textarea', 2)}
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-white/50 text-xs mb-1.5 font-source-sans uppercase tracking-wide">Enlaces del podcast</label>
                  <p className="text-white/35 text-xs font-source-sans">Opcional. Se mostrarán como botones en la tarjeta del portafolio.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {PLATFORM_FIELDS.map(({ key, label }) => (
                    <div key={key}>
                      <label className="block text-white/50 text-xs mb-1.5 font-source-sans uppercase tracking-wide">{label}</label>
                      <input
                        value={platforms[key]}
                        onChange={e => setPlatforms(current => ({ ...current, [key]: e.target.value }))}
                        className="w-full bg-[#0F1C2E] border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:border-[#7D468C]"
                        placeholder={`https://${label.toLowerCase().replace(/\s+/g, '')}.com/...`}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="pub-case" checked={form.published} onChange={e => setForm(f => ({ ...f, published: e.target.checked }))} className="accent-[#7D468C]" />
                <label htmlFor="pub-case" className="text-white/60 text-sm font-source-sans">Publicado</label>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving} className="bg-[#4EBFBF] text-white px-6 py-2.5 font-poppins font-semibold text-sm hover:bg-[#76DDDD] disabled:opacity-50">
                  {saving ? 'Guardando...' : (editing ? 'Actualizar' : 'Crear Caso')}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className="border border-white/20 text-white/60 px-6 py-2.5 font-poppins text-sm hover:border-white/40">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCases;
